import React, { useState } from 'react';
import {
  Search,
  Filter,
  Download,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Ban,
  Euro,
  X,
  FileText
} from 'lucide-react';

interface Invoice {
  id: string;
  number: string;
  clientName: string;
  company: string;
  vatEntity: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'cancelled';
  dueDate: string;
  issueDate: string;
  bookingId: string;
}

const mockInvoices: Invoice[] = [
  {
    id: 'INV001',
    number: 'JAIMY-2025-001',
    clientName: 'John Doe',
    company: 'Belfius',
    vatEntity: 'Jaimy BV',
    amount: 1250.00,
    status: 'paid',
    dueDate: '2025-03-20',
    issueDate: '2025-02-20',
    bookingId: 'B001'
  },
  {
    id: 'INV002',
    number: 'JAIMY-2025-002',
    clientName: 'Alice Johnson',
    company: 'DVV',
    vatEntity: 'Jaimy Services BVBA',
    amount: 850.00,
    status: 'pending',
    dueDate: '2025-03-21',
    issueDate: '2025-02-21',
    bookingId: 'B002'
  }
];

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const statusIcons = {
  paid: CheckCircle2,
  pending: Clock,
  overdue: AlertCircle,
  cancelled: Ban
};

const vatEntities = ['Jaimy BV', 'Jaimy Services BVBA', 'Jaimy International SA'];
const companies = ['Belfius', 'DVV', 'Europ Assistance', 'Verisure'];
const dateRanges = ['This Month', 'Last Month', 'This Quarter', 'Last Quarter', 'This Year', 'Custom Range'];

export function InvoiceList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: [] as string[],
    vatEntity: [] as string[],
    company: [] as string[],
    dateRange: ''
  });

  const filteredInvoices = mockInvoices.filter(invoice => {
    const matchesSearch = 
      invoice.number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.bookingId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filters.status.length === 0 || filters.status.includes(invoice.status);
    const matchesVatEntity = filters.vatEntity.length === 0 || filters.vatEntity.includes(invoice.vatEntity);
    const matchesCompany = filters.company.length === 0 || filters.company.includes(invoice.company);

    return matchesSearch && matchesStatus && matchesVatEntity && matchesCompany;
  });

  const handleFilterChange = (type: keyof typeof filters, value: string) => {
    setFilters(prev => {
      const array = prev[type] as string[];
      const newArray = array.includes(value)
        ? array.filter(item => item !== value)
        : [...array, value];
      return { ...prev, [type]: newArray };
    });
  };

  const handleDownload = (invoiceId: string) => {
    // In a real application, this would trigger a download of the invoice PDF
    console.log(`Downloading invoice ${invoiceId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Manage and track all invoices</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFiltersOpen(true)}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters Modal */}
      {isFiltersOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Filter Invoices</h2>
              <button
                onClick={() => setIsFiltersOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Status Filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Payment Status</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusColors).map(([status, colors]) => {
                    const StatusIcon = statusIcons[status as keyof typeof statusIcons];
                    return (
                      <button
                        key={status}
                        onClick={() => handleFilterChange('status', status)}
                        className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          filters.status.includes(status) ? colors : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <StatusIcon className="h-4 w-4 mr-1.5" />
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* VAT Entity Filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">VAT Entity</h3>
                <div className="flex flex-wrap gap-2">
                  {vatEntities.map((entity) => (
                    <button
                      key={entity}
                      onClick={() => handleFilterChange('vatEntity', entity)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filters.vatEntity.includes(entity)
                          ? 'bg-jaimy-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FileText className="h-4 w-4 mr-1.5" />
                      {entity}
                    </button>
                  ))}
                </div>
              </div>

              {/* Company Filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Company</h3>
                <div className="flex flex-wrap gap-2">
                  {companies.map((company) => (
                    <button
                      key={company}
                      onClick={() => handleFilterChange('company', company)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filters.company.includes(company)
                          ? 'bg-jaimy-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Building2 className="h-4 w-4 mr-1.5" />
                      {company}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Date Range</h3>
                <div className="flex flex-wrap gap-2">
                  {dateRanges.map((range) => (
                    <button
                      key={range}
                      onClick={() => handleFilterChange('dateRange', range)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        filters.dateRange === range
                          ? 'bg-jaimy-red text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Calendar className="h-4 w-4 mr-1.5" />
                      {range}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setFilters({
                    status: [],
                    vatEntity: [],
                    company: [],
                    dateRange: ''
                  });
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Reset Filters
              </button>
              <button
                onClick={() => setIsFiltersOpen(false)}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Invoice Number</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Client</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">VAT Entity</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Issue Date</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Due Date</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredInvoices.map((invoice) => {
                const StatusIcon = statusIcons[invoice.status];
                return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">{invoice.number}</div>
                        <div className="ml-2 text-xs text-gray-500">({invoice.bookingId})</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{invoice.clientName}</div>
                          <div className="text-sm text-gray-500">{invoice.company}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{invoice.vatEntity}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm font-medium text-gray-900">
                        <Euro className="h-4 w-4 mr-1" />
                        {invoice.amount.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(invoice.issueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusColors[invoice.status]
                      }`}>
                        <StatusIcon className="h-4 w-4 mr-1" />
                        {invoice.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDownload(invoice.id)}
                        className="text-gray-400 hover:text-jaimy-red transition-colors"
                        title="Download Invoice"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}